/**
 * Inline thumbs up/down widget for AI responses.
 *
 * Usage:
 *   <AIFeedbackWidget sourceType="whatsapp" sourceId={conversationId} aiText={message.body} />
 */
import { useState } from 'react';

const token = () => localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token');

const AIFeedbackWidget = ({ sourceType, sourceId, aiText, compact = false }) => {
  const [submitted, setSubmitted] = useState(null); // null | 'thumbs_up' | 'thumbs_down'
  const [showForm, setShowForm] = useState(false);
  const [category, setCategory] = useState('');
  const [correction, setCorrection] = useState('');

  const submit = async (rating, extra = {}) => {
    try {
      await fetch('/feedback/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          source_type: sourceType,
          source_id: sourceId ? String(sourceId) : null,
          ai_response_text: aiText || null,
          rating,
          ...extra,
        }),
      });
      setSubmitted(rating);
      setShowForm(false);
    } catch (_err) {
      // Silently fail — feedback is non-critical
    }
  };

  if (submitted) {
    return (
      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', userSelect: 'none' }}>
        {submitted === 'thumbs_up' ? '👍 Thanks!' : '👎 Noted'}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <button
        onClick={() => submit('thumbs_up')}
        title="Good response"
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: compact ? '0.8rem' : '1rem', padding: '2px', opacity: 0.6, transition: 'opacity 0.15s' }}
        onMouseEnter={(e) => { e.target.style.opacity = 1; }}
        onMouseLeave={(e) => { e.target.style.opacity = 0.6; }}
      >
        👍
      </button>
      <button
        onClick={() => setShowForm(!showForm)}
        title="Poor response"
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: compact ? '0.8rem' : '1rem', padding: '2px', opacity: 0.6, transition: 'opacity 0.15s' }}
        onMouseEnter={(e) => { e.target.style.opacity = 1; }}
        onMouseLeave={(e) => { e.target.style.opacity = 0.6; }}
      >
        👎
      </button>

      {showForm && (
        <span style={{
          display: 'inline-flex', flexDirection: 'column', gap: '6px',
          padding: '10px', background: 'var(--bg-secondary)', borderRadius: '8px',
          border: '1px solid rgba(100,116,139,0.2)', marginLeft: '6px', minWidth: '220px'
        }}>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #ccc', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.8rem' }}
          >
            <option value="">Select reason (optional)</option>
            <option value="wrong_info">Wrong information</option>
            <option value="tone">Inappropriate tone</option>
            <option value="irrelevant">Irrelevant response</option>
            <option value="other">Other</option>
          </select>
          <input
            type="text"
            placeholder="Correction (optional)"
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #ccc', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.8rem' }}
          />
          <button
            onClick={() => submit('thumbs_down', { category: category || null, correction_text: correction || null })}
            style={{ padding: '5px 10px', background: '#ff6b6b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
          >
            Submit Feedback
          </button>
        </span>
      )}
    </span>
  );
};

export default AIFeedbackWidget;
